import nacl from 'tweetnacl'; // For ED25519 signing
import { Buffer } from 'buffer';
import {
    mnemonicNew,
    mnemonicValidate,
    mnemonicToWalletKey
} from '@ton/crypto';
import { beginCell, Cell, external, loadMessageRelaxed, MessageRelaxed, storeMessage, WalletContractV4 } from '@ton/ton';
import bs58 from 'bs58';
import { validateMnemonic, wordlists } from 'bip39';

import { 
    getUserTelegramId,
    SecureStorage, 
    // CloudStorage 
} from './telegram';
import { client } from './api';
import { IncompatibleWalletError, InvalidMnemonicError, MissingDataError, NotLoggedInError } from './error';

import * as Sentry from "@sentry/react"
import { CaptchaRequiredResponse } from '../types/captcha';

// If you also need TonWeb for on-chain calls, you could import it:
// import TonWeb from 'tonweb';

// Polyfill: ensure Buffer is available (if in a browser environment)
if (typeof window !== 'undefined') {
    (window as any).Buffer = Buffer;
    (window as any).process = { env: {} };
}

// Common storage keys
const MNEMONIC_KEY = 'ton_mnemonic';
const SECRET_KEY = 'ton_secret_key';
const PIN_KEY = 'ton_pin';
const TOKEN_KEY = 'ton_token';
const TOKEN_VALIDITY_KEY = 'ton_token_valid_until';
const NODE_ENV = import.meta.env.VITE_ENV;

/**
 * Encryption utility for sensitive data
 */
export class CryptoUtils {
    /**
     * Derives an encryption key based on user-specific data
     */
    static async getEncryptionKey(): Promise<CryptoKey> {
        // Use Telegram user ID or other unique identifier as salt
        const webApp = window.Telegram?.WebApp;
        const salt = webApp?.initDataUnsafe?.user?.id?.toString() || 'default-salt';
        
        // Create a consistent passphrase (could be enhanced with app-specific secret)
        const passphrase = `jwallet-ton-telegram-${salt}`;
        
        // Convert passphrase to bytes
        const encoder = new TextEncoder();
        const passphraseBytes = encoder.encode(passphrase);
        
        // Derive a key using PBKDF2
        const keyMaterial = await window.crypto.subtle.importKey(
            'raw',
            passphraseBytes,
            { name: 'PBKDF2' },
            false,
            ['deriveBits', 'deriveKey']
        );
        
        // Create salt from the passphrase for simplicity
        const saltBytes = encoder.encode(salt);
        
        // Derive the actual encryption key
        return window.crypto.subtle.deriveKey(
            {
                name: 'PBKDF2',
                salt: saltBytes,
                iterations: 100000,
                hash: 'SHA-256'
            },
            keyMaterial,
            { name: 'AES-GCM', length: 256 },
            false,
            ['encrypt', 'decrypt']
        );
    }
    
    /**
     * Encrypts a string value using AES-GCM
     */
    static async encrypt(value: string): Promise<string> {
        try {
            const key = await this.getEncryptionKey();
            const encoder = new TextEncoder();
            const dataBytes = encoder.encode(value);
            
            // Create a random initialization vector
            const iv = window.crypto.getRandomValues(new Uint8Array(12));
            
            // Encrypt the data
            const encryptedData = await window.crypto.subtle.encrypt(
                {
                    name: 'AES-GCM',
                    iv
                },
                key,
                dataBytes
            );
            
            // Combine IV and encrypted data into a single array
            const result = new Uint8Array(iv.length + encryptedData.byteLength);
            result.set(iv, 0);
            result.set(new Uint8Array(encryptedData), iv.length);
            
            // Convert to base64 for storage
            return btoa(String.fromCharCode(...result));
        } catch (error) {
            Sentry.captureException(error)
            return value; // Fallback to unencrypted value
        }
    }
    
    /**
     * Decrypts a string value using AES-GCM
     */
    static async decrypt(encryptedValue: string): Promise<string> {
        try {
            // Convert from base64
            const encryptedBytes = Uint8Array.from(atob(encryptedValue), c => c.charCodeAt(0));
            
            // Extract IV (first 12 bytes)
            const iv = encryptedBytes.slice(0, 12);
            const data = encryptedBytes.slice(12);
            
            const key = await this.getEncryptionKey();
            
            // Decrypt the data
            const decryptedData = await window.crypto.subtle.decrypt(
                {
                    name: 'AES-GCM',
                    iv
                },
                key,
                data
            );
            
            // Convert back to string
            const decoder = new TextDecoder();
            return decoder.decode(decryptedData);
        } catch (error) {
            Sentry.captureException(error)
            return encryptedValue; // Return the encrypted value as fallback
        }
    }
}

/**
 * Minimal local keystore for storing TON mnemonic & secretKey. 
 * Adjust as necessary for your security model.
 */
export class TonWalletUtils {
    /** SecureStorage methods */
    static async storePin(pin: string): Promise<boolean> {
        try {
            const success = await SecureStorage.setItem(PIN_KEY, pin);
            if (!success) {
                throw new Error('SecureStorage.setItem returned false for PIN');
            }
            // Verify it was actually saved
            const verification = await SecureStorage.getItem(PIN_KEY);
            if (verification !== pin) {
                throw new Error('PIN verification failed after storage');
            }
            return true;
        } catch (error) {
            Sentry.captureException(error)
            return false;
        }
    }
    static async checkPin(pin: string) {
        try {
            const storedPin = await SecureStorage.getItem(PIN_KEY);
            return storedPin === pin;
        } catch (error) {
            Sentry.captureException(error)
        }
    }
    static async storeSecret(secretKey: Uint8Array): Promise<boolean> {
        const secretKeyBase64 = Buffer.from(secretKey).toString('base64');
        try {
            const success = await SecureStorage.setItem(SECRET_KEY, secretKeyBase64);
            if (!success) {
                throw new Error('SecureStorage.setItem returned false for secret key');
            }
            // Verify it was actually saved
            const verification = await SecureStorage.getItem(SECRET_KEY);
            if (verification !== secretKeyBase64) {
                throw new Error('Secret key verification failed after storage');
            }
            return true;
        } catch (error) {
            Sentry.captureException(error)
            return false;
        }
    }
    static async storeMnemonic(mnemonic: string): Promise<boolean> {
        try {
            const success = await SecureStorage.setItem(MNEMONIC_KEY, mnemonic);
            if (!success) {
                throw new Error('SecureStorage.setItem returned false for mnemonic');
            }
            // Verify it was actually saved
            const verification = await SecureStorage.getItem(MNEMONIC_KEY);
            if (verification !== mnemonic) {
                throw new Error('Mnemonic verification failed after storage');
            }
            return true;
        } catch (error) {
            Sentry.captureException(error)
            return false;
        }
    }
    static async getMnemonic(): Promise<string | undefined> {
        try {
            const mnemonic = await SecureStorage.getItem(MNEMONIC_KEY);
            if (mnemonic) {
                return mnemonic;
            }
        } catch (error) {
            Sentry.captureException(error)
        }
    }
    static async clear(): Promise<void> {
        try {
            await SecureStorage.removeItem(MNEMONIC_KEY);
            await SecureStorage.removeItem(SECRET_KEY);
            await SecureStorage.removeItem(PIN_KEY);
        } catch (error) {
            Sentry.captureException(error)
        }
    }
    static async hasWallet() {
        try {
            const secureKey = await SecureStorage.getItem(SECRET_KEY);
            if (secureKey) {
                return true;
            }
        } catch (error) {
            Sentry.captureException(error)
        }
    }
    static async storeToken(token: string, validUntil: number) {
        try {
            await SecureStorage.setItem(TOKEN_KEY, token);
            await SecureStorage.setItem(TOKEN_VALIDITY_KEY, validUntil.toString());
            return;
        } catch (error) {
            Sentry.captureException(error)
        }
    }
    static async getToken() {
        try {
            const token = await SecureStorage.getItem(TOKEN_KEY);
            return token;
        } catch (error) {
            Sentry.captureException(error)
        }
    }
    static async getTokenValidity() {
        try {
            const validUntil = await SecureStorage.getItem(TOKEN_VALIDITY_KEY);
            return validUntil ? parseInt(validUntil) : null;
        } catch (error) {
            Sentry.captureException(error)
        }
    }
    static async clearToken() {
        try {
            await SecureStorage.removeItem(TOKEN_KEY);
            await SecureStorage.removeItem(TOKEN_VALIDITY_KEY);
        } catch (error) {
            Sentry.captureException(error)
        }
    }

    /** Temporary dev only LocalStorage methods */
    // static async storePin(pin: string): Promise<void> {
    //     try {
    //         localStorage.setItem(PIN_KEY, await CryptoUtils.encrypt(pin));
    //         return; // Successfully stored in LocalStorage
    //     } catch (error) {
    //         console.warn('LocalStorage failed', error);
    //     }
    // }
    // static async checkPin(pin: string): Promise<boolean> {
    //     try {
    //         const storedPin = localStorage.getItem(PIN_KEY);
    //         if (storedPin) {
    //             return (await CryptoUtils.decrypt(storedPin)) === pin;
    //         }
    //         return false;
    //     } catch (error) {
    //         console.warn('Error checking CloudStorage', error);
    //         return false;
    //     }
    // }
    // static async storeSecret(secretKey: Uint8Array): Promise<void> {
    //     const secretKeyBase64 = Buffer.from(secretKey).toString('base64');
    //     try {
    //         const encrypted = await CryptoUtils.encrypt(secretKeyBase64)
    //         localStorage.setItem(SECRET_KEY, encrypted);
    //         return; // Successfully stored in LocalStorage
    //     } catch (error) {
    //         console.warn('LocalStorage failed', error);
    //     }
    // }
    // static async storeMnemonic(mnemonic: string): Promise<void> {
    //     try {
    //         localStorage.setItem(MNEMONIC_KEY, await CryptoUtils.encrypt(mnemonic));
    //         return; // Successfully stored in CloudStorage
    //     } catch (error) {
    //         console.warn('CloudStorage failed', error);
    //     }
    // }
    // static async getMnemonic(): Promise<string | undefined> {
    //     try {
    //         const mnemonic = localStorage.getItem(MNEMONIC_KEY);
    //         if (mnemonic) {
    //             return (await CryptoUtils.decrypt(mnemonic));
    //         }
    //     } catch (error) {
    //         console.warn('Error retrieving from LocalStorage', error);
    //     }
    //     return undefined;
    // }
    // static clear() {
    //    // Clear from LocalStorage
    //    try {
    //         localStorage.removeItem(MNEMONIC_KEY);
    //         localStorage.removeItem(SECRET_KEY);
    //         localStorage.removeItem(PIN_KEY);
    //     } catch (error) {
    //         console.warn('Error clearing LocalStorage', error);
    //     }
    // }
    // static hasWallet() {
    //     try {
    //         const secretKey = localStorage.getItem(SECRET_KEY);
    //         if (secretKey) {
    //             return true;
    //         }
    //     } catch (error) {
    //         console.warn('Error checking LocalStorage', error);
    //     }
    //     return false;
    // }
    // static async storeToken(token: string, validUntil: number) {
    //     try {
    //         localStorage.setItem(TOKEN_KEY, await CryptoUtils.encrypt(token));
    //         // -1 minute to prevent case where local valid but server invalid
    //         localStorage.setItem(TOKEN_VALIDITY_KEY, await CryptoUtils.encrypt((validUntil - 60000).toString()));
    //         return;
    //     } catch (error) {
    //         console.warn('Error storing in LocalStorage', error);
    //     }
    // }
    // static async getToken() {
    //     try {
    //         const token = localStorage.getItem(TOKEN_KEY);
    //         if (token) {
    //             return (await CryptoUtils.decrypt(token));
    //         }
    //     } catch (error) {
    //         console.warn('Error retrieving from LocalStorage', error);
    //     }
    // }
    // static async getTokenValidity() {
    //     try {
    //         const validUntil = localStorage.getItem(TOKEN_VALIDITY_KEY);
    //         return validUntil ? parseInt(await CryptoUtils.decrypt(validUntil)) : null;
    //     } catch (error) {
    //         console.warn('Error retrieving from LocalStorage', error);
    //     }
    // }
    // static clearToken() {
    //     try {
    //         localStorage.removeItem(TOKEN_KEY);
    //         localStorage.removeItem(TOKEN_VALIDITY_KEY);
    //     } catch (error) {
    //         console.warn('Error clearing from LocalStorage', error);
    //     }
    // }
    static async hasTokenExpired() {
        const tokenValidity = await this.getTokenValidity()
        if (!tokenValidity) {
            return true
        }
        return tokenValidity < Date.now()
    }
    static async getValidToken() {
        if (!(await this.hasTokenExpired()) && (await this.hasWallet())) {
            const token = await this.getToken()
            if (token) {
                return token
            } else {
                throw new NotLoggedInError('No token available')
            }
        } else {
            throw new NotLoggedInError('Token invalid')
        }
    }

    static async getPublicKey() {
        const mnemonic = await this.getMnemonic()
        if (!mnemonic) {
            return null;
        }
        const key = await mnemonicToWalletKey(mnemonic.trim().split(' '));
        return key.publicKey
    }

    static async getBufferPublicKey() {
        const key = await this.getPublicKey()
        if (!key) {
            return null;
        }
        return Buffer.from(key)
    }

    static async getBase64PublicKey() {
        const key = await this.getBufferPublicKey()
        if (!key) {
            return null;
        }
        return key.toString('base64');
    }

    static async getHexPublicKey() {
        const key = await this.getBufferPublicKey()
        if (!key) {
            return null;
        }
        return key.toString('hex');
    }

    static async getV4PublicAddress() {
        const wallet = await this.getV4Wallet()
        if (!wallet) {
            return null;
        }
        return wallet.address.toString({
            bounceable: false,
            testOnly: NODE_ENV !== 'production'
        })
    }

    static async getSecretKey() {
        const mnemonic = await this.getMnemonic()
        if (!mnemonic) {
            return null;
        }
        const keyPair = await mnemonicToWalletKey(mnemonic.trim().split(/\s+/));
        return Buffer.from(new Uint8Array(keyPair.secretKey))
    }

    static async getV4Wallet() {
        const key = await this.getBufferPublicKey()
        if (!key) {
            return null;
        }
        return WalletContractV4.create({
            publicKey: key,
            workchain: 0
        })
    }

    static async getBase58PublicKey() {
        const key = await this.getBufferPublicKey()
        if (!key) {
            return null;
        }
        return bs58.encode(key);
    }

    static async generateWalletData(
        pin: string,
        wordCount?: number
    ) {
        // 1. Generate new mnemonic
        const words = await mnemonicNew(wordCount || 24);
    
        // 2. Validate that it's correct
        const valid = await mnemonicValidate(words);
        if (!valid) {
            throw new Error('Failed to generate a valid mnemonic');
        }
    
        // 3. Derive key from mnemonic
        const key = await mnemonicToWalletKey(words);
    
        // 4. Return wallet data without storing
        return {
            pin,
            mnemonic: words.join(' '),
            secretKey: key.secretKey
        };
    }

    static async storeWalletData(walletData: {pin: string, mnemonic: string, secretKey: Uint8Array}): Promise<void> {
        // Store all wallet data with verification
        // Throws on failure after clearing all data
        const pinStored = await this.storePin(walletData.pin);
        const mnemonicStored = await this.storeMnemonic(walletData.mnemonic);
        const secretStored = await this.storeSecret(walletData.secretKey);

        // If any storage failed, clear everything and throw
        if (!pinStored || !mnemonicStored || !secretStored) {
            await this.clear();
            throw new Error('Failed to store wallet data securely');
        }
    }

    static async createTonWallet(
        pin: string,
        wordCount?: number
    ) {
        // Generate and immediately store wallet data (legacy method)
        const walletData = await this.generateWalletData(pin, wordCount);
        await this.storeWalletData(walletData);
    }

    static async generateRecoveryWalletData(mnemonic: string) {
        const words = mnemonic.trim().split(' ');
    
        // First check if it's a valid TON mnemonic
        const validTon = await mnemonicValidate(words);
        
        if (!validTon) {
            // If not valid for TON, check if it's valid BIP39 (likely Solana)
            const validBip39 = validateMnemonic(words.join(' '), wordlists.english);
            if (validBip39) {
                throw new IncompatibleWalletError('Incompatible wallet: This appears to be a Solana wallet');
            } else {
                throw new InvalidMnemonicError('Invalid mnemonic phrase');
            }
        }
        
        // If we get here, it's a valid TON mnemonic
        // Try to derive key from mnemonic
        try {
            const key = await mnemonicToWalletKey(words);
            
            // Return wallet data without storing
            return {
                mnemonic: mnemonic,
                secretKey: key.secretKey
            };
        } catch (error) {
            throw new InvalidMnemonicError('Failed to derive key from mnemonic');
        }
    }

    static async recoverTonWallet(
        mnemonic: string
    ) {
        const words = mnemonic.trim().split(' ');
    
        // First check if it's a valid TON mnemonic
        const validTon = await mnemonicValidate(words);
        
        if (!validTon) {
            // If not valid for TON, check if it's valid BIP39 (likely Solana)
            const validBip39 = validateMnemonic(words.join(' '), wordlists.english);
            if (validBip39) {
                throw new IncompatibleWalletError('Incompatible wallet: This appears to be a Solana wallet');
            } else {
                throw new InvalidMnemonicError('Invalid mnemonic phrase');
            }
        }
        
        // If we get here, it's a valid TON mnemonic
        // Try to derive key from mnemonic
        try {
            const key = await mnemonicToWalletKey(words);
            
            // Store in local storage
            this.storeMnemonic(mnemonic);
            this.storeSecret(key.secretKey);
        } catch (error) {
            throw new InvalidMnemonicError('Failed to derive key from mnemonic');
        }
    }

    static async exportPrivateKey() {
        // 1. Get mnemonic from local storage
        const mnemonic = await this.getMnemonic();
        if (!mnemonic) {
            throw new Error('No mnemonic phrase found in storage');
        }
        const words = mnemonic.trim().split(' ');
        const valid = await mnemonicValidate(words);
        if (!valid) {
            throw new Error('Invalid mnemonic phrase in storage');
        }
    
        // 2. Derive key
        const key = await mnemonicToWalletKey(words);
    
        // 3. Return hex or base64
        return Buffer.from(key.secretKey).toString('base64');
    }

    static async signData(data: string): Promise<string> {
        const mnemonic = await this.getMnemonic();
        if (!mnemonic) {
            throw new Error('No mnemonic phrase found in storage');
        }
        const secretKeyBytes = (await mnemonicToWalletKey(mnemonic.trim().split(' '))).secretKey
        if (!secretKeyBytes) {
            throw new Error('No secret key found in storage');
        }
        const dataBuffer = Buffer.from(data, 'utf8');
        const signature = nacl.sign.detached(dataBuffer, secretKeyBytes);
        return Buffer.from(signature).toString('base64');
    }

    static async signDataBase64(base64Data: string): Promise<string> {
        // Convert input data from base64 to a UTF-8 string
        const dataBuffer = Buffer.from(base64Data, 'base64');
        return this.signData(dataBuffer.toString('utf8'));
    }

    private static async performWalletAuth(
        endpoint: string,
        walletData: {pin: string, mnemonic: string, secretKey: Uint8Array}, 
        captcha_key?: string, 
        slide_x?: number, 
        slide_y?: number
    ) {
        const time = Date.now()
        
        // Derive key and public key from wallet data
        const key = await mnemonicToWalletKey(walletData.mnemonic.trim().split(' '));
        const publicKeyBuffer = Buffer.from(key.publicKey);
        const publicKeyBase58 = bs58.encode(publicKeyBuffer);
        
        // Sign the timestamp with the secret key
        const dataBuffer = Buffer.from(`${time}`, 'utf8');
        const signature = nacl.sign.detached(dataBuffer, walletData.secretKey);
        const signatureBase64 = Buffer.from(signature).toString('base64');
        
        const body: any = {
            public_key: publicKeyBase58,
            timestamp: time,
            signature: signatureBase64,
            telegram_id: getUserTelegramId()
        }
        
        if (captcha_key && slide_x !== undefined && slide_y !== undefined) {
            body.captcha_key = captcha_key;
            body.captcha_value = {
                slide_x: slide_x,
                slide_y: slide_y
            };
        }
        
        const response = await client.doUnauthenticatedPost(endpoint, body)

        // Check if captcha is required (explicit flag)
        if (response && response.captcha_required) {
            return response as CaptchaRequiredResponse
        }

        // Check if this is a captcha validation failure response (captcha data without explicit flag)
        if (response && response.captcha_key && response.master_image_base64 && response.thumb_image_base64) {
            return {
                captcha_required: true,
                data: response
            } as CaptchaRequiredResponse
        }

        // If we get here, captcha validation succeeded or no captcha was required
        if (response && response.Bearer && response.expires_at) {
            await this.storeToken(response.Bearer, response.expires_at)
            return response
        } else {
            throw new Error('Invalid response from server')
        }
    }

    static async registerWalletWithData(walletData: {pin: string, mnemonic: string, secretKey: Uint8Array}, captcha_key?: string, slide_x?: number, slide_y?: number) {
        return this.performWalletAuth('/wallet/register', walletData, captcha_key, slide_x, slide_y);
    }

    static async registerWallet(captcha_key?: string, slide_x?: number, slide_y?: number) {
        if (await this.hasWallet()) {
            const time = Date.now()
            const body: any = {
                public_key: await this.getBase58PublicKey(),
                timestamp: time,
                signature: await this.signData(`${time}`),
                telegram_id: getUserTelegramId()
            }
            
            if (captcha_key && slide_x !== undefined && slide_y !== undefined) {
                body.captcha_key = captcha_key;
                body.captcha_value = {
                    slide_x: slide_x,
                    slide_y: slide_y
                };
            }
            
            const response = await client.doUnauthenticatedPost('/wallet/register', body)

            // Check if captcha is required (explicit flag)
            if (response && response.captcha_required) {
                return response as CaptchaRequiredResponse
            }

            // Check if this is a captcha validation failure response (captcha data without explicit flag)
            if (response && response.captcha_key && response.master_image_base64 && response.thumb_image_base64) {
                return {
                    captcha_required: true,
                    data: response
                } as CaptchaRequiredResponse
            }

            // If we get here, captcha validation succeeded or no captcha was required
            if (response && response.Bearer && response.expires_at) {
                await this.storeToken(response.Bearer, response.expires_at)
                return response
            } else {
                throw new Error('Invalid response from server')
            }
        } else {
            throw new MissingDataError('No wallet data found in storage')
        }
    }
    static async loginWalletWithData(walletData: {pin: string, mnemonic: string, secretKey: Uint8Array}, captcha_key?: string, slide_x?: number, slide_y?: number) {
        return this.performWalletAuth('/wallet/register', walletData, captcha_key, slide_x, slide_y);
    }

    static async authenticateExistingWallet(captcha_key?: string, slide_x?: number, slide_y?: number) {
        if (await this.hasWallet()) {
            const time = Date.now()
            const body: any = {
                public_key: await this.getBase58PublicKey(),
                timestamp: time,
                signature: await this.signData(`${time}`),
                telegram_id: getUserTelegramId()
            }
            
            if (captcha_key && slide_x !== undefined && slide_y !== undefined) {
                body.captcha_key = captcha_key;
                body.captcha_value = {
                    slide_x: slide_x,
                    slide_y: slide_y
                };
            }
            
            const response = await client.doUnauthenticatedPost('/wallet/register', body)

            // Check if captcha is required (explicit flag)
            if (response && response.captcha_required) {
                return response as CaptchaRequiredResponse
            }

            // Check if this is a captcha validation failure response (captcha data without explicit flag)
            if (response && response.captcha_key && response.master_image_base64 && response.thumb_image_base64) {
                return {
                    captcha_required: true,
                    data: response
                } as CaptchaRequiredResponse
            }

            // If we get here, captcha validation succeeded or no captcha was required
            if (response && response.Bearer && response.expires_at) {
                await this.storeToken(response.Bearer, response.expires_at)
                return response
            } else {
                throw new Error('Invalid response from server')
            }
        } else {
            throw new MissingDataError('No wallet data found in storage')
        }
    }

    static async refreshToken() {
        if (await this.hasWallet()) {
            const time = Date.now()
            const body = {
                public_key: await this.getBase58PublicKey(),
                timestamp: time,
                signature: await this.signData(`${time}`)
            }
            const { Bearer, expires_at } = await client.doUnauthenticatedPost('/wallet/auth', body)
            await this.storeToken(Bearer, expires_at)
        } else {
            throw new MissingDataError('No wallet data found in storage')
        }
    }
    static decodeMessage(message: string): MessageRelaxed {
        const [root] = Cell.fromBoc(Buffer.from(message, 'base64'))
        return loadMessageRelaxed(root.beginParse())
    }
    static async signMessage(message: string, seqno: number) {
        // Step 1: Decode the Base64-encoded message into a MessageRelaxed object
        // This converts the raw message string into a structured message that TON can process
        const internalMessage = this.decodeMessage(message)
        
        // Step 2: Get the user's wallet contract
        // This retrieves the WalletContractV4 instance that represents the user's wallet on TON
        const wallet = await this.getV4Wallet()
        
        // Step 3: Get the user's private key
        // This retrieves the secret key needed to sign the transaction
        const secretKey = await this.getSecretKey()
        
        // Step 4: Verify wallet and key existence
        // If either the wallet or private key is missing, we cannot sign the message
        if (!wallet || !secretKey) {
            throw new Error('No wallet found in storage')
        }
        
        // Step 5: Create the transfer body
        // This uses the wallet contract to generate the correct transaction body
        // It includes the sequence number, signs with the private key, and attaches our message
        const body = wallet.createTransfer({
            seqno,              // Sequence number to prevent replay attacks
            secretKey,          // Private key to cryptographically sign the transfer
            messages: [internalMessage]  // The actual message payload to be sent
        })
        
        // Step 6: Wrap in an external message
        // External messages are how transactions are submitted to the TON blockchain
        // They contain the destination address and the body created above
        const externalMessage = external({
            to: wallet.address,  // The destination wallet address
            body                 // The transfer body created and signed above
        })
        
        // Step 7: Serialize to a Cell and convert to Base64
        // This encodes the entire message into the binary Cell format used by TON
        // Then converts it to a Base64 string that can be transmitted over the network
        const cell = beginCell().store(storeMessage(externalMessage)).endCell()
        return cell.toBoc({ idx: false }).toString('base64')
    }
}
